import { getDownloadURL, ref } from "firebase/storage";
import { Download, Loader2, RotateCw } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { Button } from "components/ui/button";
import { storage } from "integrations/firebase/storage";

export type ResourceDownload =
  | { status: "loading" }
  | { status: "ready"; url: string }
  | { status: "error"; retry: () => void };

/**
 * The download URL for a resource file, fetched as soon as the row renders.
 *
 * Fetched ahead of the tap because the open control has to be a plain link
 * whose href is already set. iOS Safari, in-app browsers and an installed PWA
 * only open a new window synchronously inside the tap; a window opened after
 * awaiting getDownloadURL is refused, and with "noopener" window.open returns
 * null, so the failure was silent.
 *
 * Kept in React state only, never written to Firestore or browser storage: a
 * Firebase download URL is a bearer token, so persisting one would hand out
 * access that outlives membership. Holding it in memory for the lifetime of
 * the page is fine, because the member could already fetch it on demand.
 * storage.rules allows the read to any member the resource's audience
 * includes.
 */
export function useResourceDownload(storagePath: string): ResourceDownload {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "ready"; url: string }
    | { status: "error" }
  >({ status: "loading" });
  // Whether the automatic refetch below has been spent on the current
  // failure. Cleared by a success, a manual retry and a new path.
  const autoRetried = useRef(false);

  useEffect(() => {
    let current = true;
    setState({ status: "loading" });
    Promise.resolve()
      .then(() => getDownloadURL(ref(storage, storagePath)))
      .then((url) => {
        if (!current) return;
        if (url) autoRetried.current = false;
        setState(url ? { status: "ready", url } : { status: "error" });
      })
      .catch(() => {
        if (current) setState({ status: "error" });
      });
    return () => {
      current = false;
    };
  }, [storagePath, attempt]);

  useEffect(() => {
    autoRetried.current = false;
  }, [storagePath]);

  // A fetch that failed while the device was offline or the page was in the
  // background would otherwise wait for a tap on Retry. Fetch again by itself
  // once, when the device comes back online or the page becomes visible; a
  // second failure waits for the button, so a dead file never loops.
  useEffect(() => {
    if (state.status !== "error" || autoRetried.current) return;
    const again = () => {
      if (autoRetried.current) return;
      if (document.visibilityState === "hidden") return;
      autoRetried.current = true;
      setAttempt((n) => n + 1);
    };
    window.addEventListener("online", again);
    document.addEventListener("visibilitychange", again);
    return () => {
      window.removeEventListener("online", again);
      document.removeEventListener("visibilitychange", again);
    };
  }, [state.status]);

  useReportFailure(state.status === "error");

  const retry = useCallback(() => {
    autoRetried.current = false;
    setAttempt((n) => n + 1);
  }, []);
  return state.status === "error" ? { status: "error", retry } : state;
}

type ReportFailure = (row: string, failed: boolean) => void;

const FailureContext = createContext<ReportFailure | null>(null);

/** Tells the enclosing ResourceFailureScope whether this row's fetch failed. */
function useReportFailure(failed: boolean) {
  const report = useContext(FailureContext);
  const row = useId();
  useEffect(() => {
    if (!report) return;
    report(row, failed);
    return () => report(row, false);
  }, [report, row, failed]);
}

/** How long the failure count has to hold still before the alert shows it. */
export const FAILURE_ANNOUNCE_DELAY_MS = 300;

/**
 * Wraps a list of resource rows and announces their failed fetches in one
 * alert above the list. Each row still says it could not load, in plain text
 * beside its Retry button, but a role=alert per row would have a screen
 * reader read the same sentence once for every file when storage is down.
 *
 * The rows fail one by one, a few milliseconds apart, and every change to an
 * alert's text is read out. So the count is shown once it has held still for
 * FAILURE_ANNOUNCE_DELAY_MS, and a burst of ten failures is read as one
 * sentence instead of ten. When every row has recovered the alert goes at
 * once, since removing it reads nothing.
 */
export function ResourceFailureScope({
  children,
  testId,
}: {
  children: ReactNode;
  testId?: string;
}) {
  const [failed, setFailed] = useState<ReadonlySet<string>>(() => new Set());
  const report = useCallback<ReportFailure>((row, isFailed) => {
    setFailed((previous) => {
      if (previous.has(row) === isFailed) return previous;
      const next = new Set(previous);
      if (isFailed) next.add(row);
      else next.delete(row);
      return next;
    });
  }, []);
  const [announced, setAnnounced] = useState(0);
  useEffect(() => {
    if (failed.size === 0) {
      setAnnounced(0);
      return;
    }
    const timer = window.setTimeout(
      () => setAnnounced(failed.size),
      FAILURE_ANNOUNCE_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [failed.size]);
  const count = failed.size === 0 ? 0 : announced;
  return (
    <FailureContext.Provider value={report}>
      {count > 0 && (
        <p
          role="alert"
          className="text-sm text-destructive-text"
          data-testid={testId}
        >
          {count === 1
            ? "One file could not be loaded. Use Retry on its row."
            : `${count} files could not be loaded. Use Retry on each one.`}
        </p>
      )}
      {children}
    </FailureContext.Provider>
  );
}

/**
 * The Open control for a resource row: a real link styled as a button once
 * the URL is ready, a disabled button while it loads, and a retry button when
 * the fetch failed. A retry only fetches the URL again; the link it brings
 * back needs a second tap, since a window opened after the fetch would be
 * refused for the reason given on useResourceDownload.
 *
 * h-11 on every width: 44px is the smallest target a thumb hits reliably.
 * Every accessible name starts with the visible label (WCAG 2.5.3), so a
 * voice command saying "Open" or "Retry" reaches the button.
 */
export function ResourceOpenControl({
  download,
  name,
  testId,
}: {
  download: ResourceDownload;
  name: string;
  testId: string;
}) {
  if (download.status === "ready")
    return (
      <Button asChild variant="ghost" size="sm" className="h-11 shrink-0">
        <a
          href={download.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${name}`}
          data-testid={testId}
        >
          <Download className="h-4 w-4" />
          Open
        </a>
      </Button>
    );
  if (download.status === "error")
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-11 shrink-0"
        onClick={download.retry}
        aria-label={`Retry loading ${name}`}
        data-testid={testId}
      >
        <RotateCw className="h-4 w-4" />
        Retry
      </Button>
    );
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-11 shrink-0"
      disabled
      aria-label={`Open ${name}, preparing`}
      data-testid={testId}
    >
      <Loader2 className="h-4 w-4 animate-spin" />
      Open
    </Button>
  );
}
